// The Findings time strip (components/FindingsTime.jsx;
// ~/Desktop/notes/design-prefs/fe-round1/findings-time.md):
// agent work over a frame twice the selected range, in calendar units fine
// enough to draw it as one continuous line, the cases in each unit hung below
// it on their own scale, and the selection that is the page's range.
import { runQueryRows } from "./analyticsClient.js";
import { MAX_RANGE_DAYS, rangeForDays, rangeSpan, spanWords } from "./shell.js";
import { parseServerTimestamp } from "./traceUtils.js";

export const DAY = 86_400_000;
const HOUR = 3_600_000;
// The frame is twice the selected range, so as much of it is the range as is
// context, held between a week and the widest window the analytics take.
export const FRAME_DAYS = MAX_RANGE_DAYS;
export const MIN_FRAME_DAYS = 7;
// Units a day divides into evenly, from an hour to the whole day.
export const UNIT_HOURS = [1, 2, 3, 6, 12, 24];
export const SEVERITIES = ["critical", "high", "medium", "low"];

const RANK = { critical: 0, high: 1, medium: 2, low: 3 };
const rank = (e) => RANK[e.severity] ?? 4;
const at = (e) => e.latestAt ?? e.firstAt;

/** The frame: n local midnights, oldest first, ending with the day of `now`. */
export function frameDays(now = Date.now(), n = FRAME_DAYS) {
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  const out = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    out.push(d.getTime());
  }
  return out;
}

/**
 * The frame for a range: twice the days it touches (between MIN_FRAME_DAYS and
 * FRAME_DAYS), the context before it, ending today or, for a range in the
 * past, half the context past its end.
 */
export function frameFor(range, now = Date.now()) {
  const { from, to } = rangeSpan(range);
  const first = new Date(from);
  first.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);
  const length = Math.round((end.getTime() - first.getTime()) / DAY) + 1;
  const n = Math.max(MIN_FRAME_DAYS, Math.min(FRAME_DAYS, 2 * length));
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  end.setDate(end.getDate() + Math.floor((n - length) / 2));
  return frameDays(Math.min(today.getTime(), end.getTime()), n);
}

/** Finds the frame day holding a moment: its index, or -1 outside the frame. */
export function dayIndexer(days) {
  const index = new Map(days.map((d, i) => [d, i]));
  return (ms) => {
    if (!Number.isFinite(ms)) return -1;
    const d = new Date(ms);
    d.setHours(0, 0, 0, 0);
    return index.get(d.getTime()) ?? -1;
  };
}

/** A local day as YYYY-MM-DD. */
export function dayKey(ms) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The smallest unit that gives each at least `minPx` of `width` across the frame's days. */
export function unitHoursFor(dayCount, width, minPx = 3) {
  for (const hours of UNIT_HOURS) if (width / ((dayCount * 24) / hours) >= minPx) return hours;
  return 24;
}

/** Each unit's start: every `hours` from each frame day's local midnight. */
export function unitStarts(days, hours) {
  const out = [];
  for (const d of days) {
    for (let h = 0; h < 24; h += hours) {
      const t = new Date(d);
      t.setHours(h, 0, 0, 0);
      out.push(t.getTime());
    }
  }
  return out;
}

/** Finds the unit holding a moment: its index, or -1 outside the frame. */
export function unitIndexer(days, hours) {
  const day = dayIndexer(days);
  const per = 24 / hours;
  return (ms) => {
    const i = day(ms);
    return i < 0 ? -1 : i * per + Math.floor(new Date(ms).getHours() / hours);
  };
}

// The frame's sessions, counted by hour so each unit starts at the reader's
// local hour. A session active across several hours counts in each, so a
// span of long sessions reads a little high.
export function sessionsSpec(days) {
  const toMs = days[days.length - 1] + DAY - 1;
  const fromMs = Math.max(days[0], toMs - FRAME_DAYS * DAY + 1);
  const T = (name) => ({ stepAlias: "t", field: `DIMENSION_FIELD_CONVERSATION_TRACE_${name}` });
  return {
    root: { alias: "t", entity: "ENTITY_TYPE_CONVERSATION_TRACE" },
    timeRange: { absolute: { start: new Date(fromMs).toISOString(), end: new Date(toMs).toISOString() } },
    output: {
      dimensions: [{ field: T("EVENT_TIME"), outputAlias: "bucket", bucketSeconds: 3600 }],
      metrics: [{ outputAlias: "sessions", computed: { field: T("TRACE_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }],
    },
    limit: FRAME_DAYS * 24 + 48,
  };
}

/** The frame's sessions by hour: [hour start ms, sessions] from the server's rows. */
export function hourlyOf(rows) {
  const out = [];
  for (const r of rows || []) {
    const ms = parseServerTimestamp(r.bucket);
    if (Number.isFinite(ms)) out.push([ms, Number(r.sessions) || 0]);
  }
  return out;
}

export async function readSessionsByHour(days, opts) {
  return hourlyOf(await runQueryRows(sessionsSpec(days), opts));
}

/** Hourly sessions ([ms, n]) summed into the frame's units. */
export function sessionsByUnit(hourly, days, hours) {
  const find = unitIndexer(days, hours);
  const out = new Array(days.length * (24 / hours)).fill(0);
  for (const [ms, n] of hourly || []) {
    const i = find(ms + HOUR / 2);
    if (i >= 0) out[i] += n;
  }
  return out;
}

/** Each unit's cases (most severe first, then newest) and their count by severity. */
export function riskByUnit(entries, days, hours) {
  const find = unitIndexer(days, hours);
  const out = Array.from({ length: days.length * (24 / hours) }, () => ({ cases: [], bySeverity: {} }));
  for (const e of entries || []) {
    const i = find(at(e));
    if (i < 0) continue;
    const sev = e.severity || "low";
    out[i].cases.push(e);
    out[i].bySeverity[sev] = (out[i].bySeverity[sev] || 0) + 1;
  }
  for (const unit of out) unit.cases.sort((a, b) => rank(a) - rank(b) || (at(b) || 0) - (at(a) || 0));
  return out;
}

// Agent work comes in bursts (on staging, one hour of a quarter held 5% of
// its sessions), so the line's full height stops at BURST times the frame's
// busy level, its 90th percentile unit, and a burst past it runs off the top.
export const BURST = 3;
/** The work line's full height: the busiest unit, or BURST times the 90th percentile unit if lower. */
export function ceilingOf(series) {
  const busy = (series || []).filter((n) => n > 0).sort((a, b) => a - b);
  if (!busy.length) return 1;
  return Math.min(busy[busy.length - 1], BURST * busy[Math.min(busy.length - 1, Math.floor(0.9 * busy.length))]);
}

/**
 * The frame units (`hours` long, whole days unless given) a range id covers,
 * as [first, last] indices, held to the frame: a range to the hour or the
 * minute covers the units its moments fall in.
 */
export function selectionOf(range, days, hours = 24) {
  const { from, to } = rangeSpan(range);
  const find = unitIndexer(days, hours);
  const last = days.length * (24 / hours) - 1;
  const end = find(to);
  const start = from < days[0] ? 0 : find(from);
  const b = end < 0 ? last : end;
  const a = start < 0 ? 0 : start;
  return [Math.min(a, b), Math.max(a, b)];
}

/**
 * The range id for frame days [a, b]: a preset when its days are exactly
 * these (by date, so a preset reaching past a short frame never matches),
 * else a custom range.
 */
export function rangeOf(a, b, days) {
  const lo = Math.max(0, Math.min(a, b));
  const hi = Math.min(days.length - 1, Math.max(a, b));
  return rangeForDays(new Date(days[lo]), new Date(days[hi]));
}

/** Whether a moment falls in a range id's span. */
export function inRange(ms, range) {
  if (!Number.isFinite(ms)) return false;
  const { from, to } = rangeSpan(range);
  return ms >= from && ms <= to;
}

const ON_DATE = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });
const ON_HOUR = new Intl.DateTimeFormat(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });

/**
 * What the strip says of units [a, b] of the frame (`starts`, each `hours`
 * long): their time (`label`; the range's own, `span`, where they stand for
 * it), their sessions (null while unread), their cases, those cases by
 * severity, most severe first, and whether they reach back before `readFrom`,
 * where the list's read stopped (`unread`), so their cases are the least
 * there are.
 */
export function wordsOf(a, b, starts, hours, sessions, risk, readFrom = null, span = null) {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  let total = sessions ? 0 : null;
  const count = new Map();
  let cases = 0;
  for (let i = lo; i <= hi; i += 1) {
    if (sessions) total += sessions[i] || 0;
    for (const e of risk[i]?.cases || []) {
      cases += 1;
      count.set(e.severity || "other", (count.get(e.severity || "other") || 0) + 1);
    }
  }
  const from = starts[lo];
  const end = new Date(starts[hi]);
  end.setHours(end.getHours() + hours);
  const to = end.getTime();
  // Whole days say their dates; part of a day says its hours.
  const whole = new Date(from).getHours() === 0 && end.getHours() === 0;
  const oneDay = dayKey(from) === dayKey(to - 1);
  const label = span ? spanWords(span.from, span.to) : !oneDay ? `${ON_DATE.format(from)} to ${ON_DATE.format(to - 1)}`
    : whole ? ON_DATE.format(from) : `${ON_DATE.format(from)}, ${ON_HOUR.format(from)} to ${ON_HOUR.format(to)}`;
  const bySeverity = [...count.entries()].sort((x, y) => (RANK[x[0]] ?? 4) - (RANK[y[0]] ?? 4));
  return { label, sessions: total, cases, bySeverity, unread: readFrom != null && from < readFrom };
}
